import type { AnalyticsSeries, ExpiringLot, SupplyRow } from '#shared/types';
import { BarChart, LineChart } from '#client/components/charts';
import { Badge } from '#client/components/ui/Badge';
import { Card } from '#client/components/ui/Card';
import { type Column, DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { formatCover, formatRupees, formatterFor } from './format';
import { heightForRows } from './report.logic';
import { toConsumptionSeries, toSeries } from './to-series';
import { EXPIRY_HORIZON_DAYS } from '#shared/types';

/** The cards of the supply section, each given only the slice of the report it shows. */

export function ConsumedCard({ series }: { series: AnalyticsSeries }) {
  return (
    <Card
      className="c-an__cell c-an__cell--wide"
      title="Reagent consumed"
      subtitle="At cost, worked out from the stock ledger"
    >
      <LineChart
        title="Reagent consumed at cost"
        series={[toSeries(series)]}
        area
        format={(value) => formatRupees(value)}
        height={240}
      />
    </Card>
  );
}

export function HeaviestUseCard({ consumers }: { consumers: readonly SupplyRow[] }) {
  return (
    <Card className="c-an__cell" title="Heaviest use" subtitle="By quantity consumed">
      <BarChart
        title="Reagents by quantity consumed"
        orientation="horizontal"
        series={[toConsumptionSeries(consumers)]}
        format={formatterFor('ratio')}
        height={heightForRows(consumers.length, 32, 200)}
        categoryLabel="Reagent"
      />
    </Card>
  );
}

/** Inside a month, an expiring lot is urgent rather than a heads-up. */
const CRITICAL_DAYS_LEFT = 30;

const EXPIRING_COLUMNS: Column<ExpiringLot>[] = [
  { key: 'item', header: 'Reagent', render: (row) => row.item_name },
  { key: 'lot', header: 'Lot', render: (row) => row.lot_no },
  {
    key: 'days',
    header: 'Days left',
    align: 'end',
    render: (row) =>
      row.days_left === null ? (
        <span>—</span>
      ) : (
        <Badge tone={row.days_left < CRITICAL_DAYS_LEFT ? 'critical' : 'warning'}>
          {row.days_left} days
        </Badge>
      ),
  },
  {
    key: 'value',
    header: 'Value',
    align: 'end',
    render: (row) => (
      <span className="tnum">{row.value === null ? '—' : formatRupees(row.value)}</span>
    ),
  },
];

export function ExpiringCard({ expiring }: { expiring: ExpiringLot[] }) {
  return (
    <Card
      className="c-an__cell"
      title="Expiring soon"
      subtitle={`Lots with stock on them, within ${EXPIRY_HORIZON_DAYS} days`}
      flush
    >
      {expiring.length === 0 ? (
        <EmptyState
          title="Nothing expiring"
          tone="positive"
          body={`No lot with stock on it expires in the next ${EXPIRY_HORIZON_DAYS} days.`}
        />
      ) : (
        <DataTable
          caption={`Lots expiring within ${EXPIRY_HORIZON_DAYS} days`}
          columns={EXPIRING_COLUMNS}
          rows={expiring}
          rowKey={(row) => row.lot_id}
        />
      )}
    </Card>
  );
}

const CONSUMPTION_COLUMNS: Column<SupplyRow>[] = [
  { key: 'name', header: 'Reagent', render: (row) => row.name },
  {
    key: 'consumed',
    header: 'Consumed',
    align: 'end',
    render: (row) => (
      <span className="tnum">
        {row.consumed_base} {row.unit}
      </span>
    ),
  },
  {
    key: 'cost',
    header: 'At cost',
    align: 'end',
    render: (row) => (
      <span className="tnum">{row.cost === null ? '—' : formatRupees(row.cost)}</span>
    ),
  },
  {
    key: 'on_hand',
    header: 'On hand',
    align: 'end',
    render: (row) => (
      <span className="tnum">
        {row.on_hand_base} {row.unit}
      </span>
    ),
  },
  {
    key: 'cover',
    header: 'Days of cover',
    align: 'end',
    // "No usage" rather than a dash or an infinity: an item nobody has used
    // has no consumption rate to project from, and that is a different
    // statement from "never runs out".
    render: (row) => <span className="tnum">{formatCover(row.days_of_cover)}</span>,
  },
];

export function ConsumptionTableCard({ consumption }: { consumption: SupplyRow[] }) {
  return (
    <Card className="c-an__cell c-an__cell--wide" title="Every reagent" flush>
      <DataTable
        caption="Reagent consumption and cover"
        columns={CONSUMPTION_COLUMNS}
        rows={consumption}
        rowKey={(row) => row.item_id}
      />
    </Card>
  );
}
